/**
 * IndexedDB-backed cache for ONNX model bytes.
 *
 * ort.InferenceSession.create() accepts either a URL (which it fetches
 * itself, every time) or a Uint8Array of pre-fetched bytes. By
 * fetching once and caching the bytes locally, second-and-later page
 * loads avoid the 25-100 MB redownload — saving 10-30 s of cold-start
 * time on phones, and not consuming bandwidth caps.
 *
 * Cache invalidation strategy:
 *   - Server is asked with `If-None-Match` once an ETag is on file.
 *     A 304 means "use cache"; a 200 means "fresh bytes, replace".
 *   - Servers without ETag fall back to a permanent cache keyed by
 *     URL — bust by appending a query string (?v=2) when shipping a
 *     new model.
 *   - URL flag `?modelCache=0` disables the cache entirely (force
 *     network on every load).
 *
 * Speed-UP step #2 of 6 from the brownfield techniques review.
 */

const DB_NAME = 'bodynodelink-modelcache';
const DB_VERSION = 1;
const STORE = 'models';

interface CachedModel {
  url: string;
  etag: string | null;
  contentLength: number | null;
  data: Uint8Array;
  fetchedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'url' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function readCached(url: string): Promise<CachedModel | null> {
  try {
    const db = await openDb();
    const rec = await new Promise<CachedModel | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(url);
      req.onsuccess = () => resolve((req.result as CachedModel) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return rec;
  } catch {
    return null;
  }
}

async function writeCached(rec: CachedModel): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(rec);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // Cache failures are non-fatal — the in-memory bytes still load.
  }
}

function cacheDisabledFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('modelCache') === '0';
}

export interface FetchModelDiagnostics {
  source: 'cache' | 'network' | 'revalidated';
  byteLength: number;
  ms: number;
  etag: string | null;
}

/**
 * Fetch model bytes with IDB-backed caching. The returned promise
 * resolves with the raw bytes ready to hand to ort. Diagnostics tell
 * the caller whether cache or network served the request, useful for
 * the HUD and logging.
 */
export async function fetchModelCached(
  url: string,
): Promise<{ bytes: Uint8Array; diag: FetchModelDiagnostics }> {
  const start = performance.now();
  if (cacheDisabledFromUrl()) {
    const bytes = await fetchFresh(url);
    return {
      bytes,
      diag: { source: 'network', byteLength: bytes.byteLength, ms: performance.now() - start, etag: null },
    };
  }

  const cached = await readCached(url);
  if (cached) {
    if (cached.etag) {
      try {
        const conditional = await fetch(url, {
          method: 'GET',
          headers: { 'If-None-Match': cached.etag },
        });
        if (conditional.status === 304) {
          return {
            bytes: cached.data,
            diag: {
              source: 'revalidated',
              byteLength: cached.data.byteLength,
              ms: performance.now() - start,
              etag: cached.etag,
            },
          };
        }
        // 200 → fresh bytes; replace the cache and return them.
        if (conditional.ok) {
          const bytes = new Uint8Array(await conditional.arrayBuffer());
          const etag = conditional.headers.get('etag');
          await writeCached({
            url,
            etag,
            contentLength: bytes.byteLength,
            data: bytes,
            fetchedAt: Date.now(),
          });
          return {
            bytes,
            diag: { source: 'network', byteLength: bytes.byteLength, ms: performance.now() - start, etag },
          };
        }
      } catch {
        // Network failure with a cached copy on file → use the cached
        // bytes. Better to load a slightly-stale model than to fail.
      }
    }
    return {
      bytes: cached.data,
      diag: {
        source: 'cache',
        byteLength: cached.data.byteLength,
        ms: performance.now() - start,
        etag: cached.etag,
      },
    };
  }

  // Cold cache: fetch and store.
  const res = await fetch(url, { method: 'GET' });
  if (!res.ok) {
    throw new Error(`fetchModelCached: ${url} → HTTP ${res.status}`);
  }
  const bytes = new Uint8Array(await res.arrayBuffer());
  const etag = res.headers.get('etag');
  await writeCached({
    url,
    etag,
    contentLength: bytes.byteLength,
    data: bytes,
    fetchedAt: Date.now(),
  });
  return {
    bytes,
    diag: { source: 'network', byteLength: bytes.byteLength, ms: performance.now() - start, etag },
  };
}

async function fetchFresh(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`fetchModelCached(no-cache): ${url} → HTTP ${res.status}`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

/** Drop a single cached model (debug helper). */
export async function evictCachedModel(url: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(url);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}

/** Drop the entire model cache. Call from devtools to force a full
 *  redownload across all models. */
export async function clearModelCache(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}
